import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { AddCircleOutline, Edit, History as HistoryIcon, LockOpen, Replay } from '@mui/icons-material';
import { endOfDay, format, startOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { setActivityDrawer } from '@store/appStore';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { currencyFormatter } from '@utils/Numbers';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { CashRegister } from '@api/CashRegister/CashRegister';
import { CashFlow } from '@api/CashFlows/CashFlow';
import { Order, OrderStatus } from '@api/Orders/Order';
import CashRegisterService from '@api/CashRegister/CashRegisterCtrl';
import CashFlowService from '@api/CashFlows/CashFlowCtrl';
import OrdersService from '@api/Orders/OrderCtrl';
import { setCashRegister } from '@store/krispisStore';
import { buildBranchCashSummaries, getCashRegisterDate, getCashRegistersDayTotals } from '@utils/CashRegisters';
import RangeDatePicker from '@components/Utilities/DatePicker/RangeDatePicker';
import ModalCashRegisterAdmin from '@screens/Dashboard/components/ModalCashRegisterAdmin';

/**
 * Panel de cajas del admin: UN día, TODAS las sucursales.
 *
 * Un renglón por CAJA (una sucursal puede tener varios turnos en el día) con su
 * propio dinero, calculado por ventana de tiempo en `@utils/CashRegisters`. Si
 * hubo movimientos fuera de toda caja, la sucursal suma un renglón "Fuera de
 * caja": ahí se ve lo que se vendió sin caja abierta.
 *
 * Aquí SÍ se ve el resultado del corte ciego (contado vs esperado); quien cierra
 * la caja nunca lo ve.
 */
interface CashRegistersTabProps {
    branchOffices: BranchOffice[];
}


/** Tope de chips en el aviso de cajas rezagadas: hubo 130 y reventaban el layout */
const MAX_PENDING_CHIPS = 12;

interface CashRow {
    id: string;
    kind: 'register' | 'outside';
    branchOfficeId: string;
    branchOfficeName: string;
    showBranchName: boolean;
    register?: CashRegister;
    isOpen: boolean;
    from?: Date;
    to?: Date;
    initialCash: number;
    cashSales: number;
    otherInputs: number;
    otherOutputs: number;
    shipping: number;
    expectedCash: number;
    countedCash?: number;
    difference?: number;
    hasBlindCount: boolean;
    ordersCount: number;
}

const CashRegistersTab: React.FunctionComponent<CashRegistersTabProps> = ({ branchOffices }) => {
    const dispatch = useDispatch();
    const { cashRegister: activeCashRegister } = useSelector((state: RootReducer) => state.krispis);
    const cashRegisterService = useMemo(() => new CashRegisterService(), []);
    const cashFlowService = useMemo(() => new CashFlowService(), []);
    const ordersService = useMemo(() => new OrdersService(), []);

    const [selectedDay, setSelectedDay] = useState<Date>(startOfDay(new Date()));
    const [cashRegisters, setCashRegisters] = useState<CashRegister[]>([]);
    const [cashFlows, setCashFlows] = useState<CashFlow[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [editing, setEditing] = useState<CashRegister | undefined>(undefined);
    const [creatingForBranch, setCreatingForBranch] = useState<string | undefined>(undefined);
    const [pendingRegisters, setPendingRegisters] = useState<CashRegister[]>([]);

    const getData = async () => {
        showLoader();
        try {
            const start = startOfDay(selectedDay);
            const end = endOfDay(selectedDay);
            const [registersData, flowsData, ordersData, pendingData] = await Promise.all([
                cashRegisterService.getCashRegistersByRange(start, end, 'all'),
                cashFlowService.getCashFlows(start, end, 'all'),
                ordersService.getOrders(OrderStatus.Completed, start, end),
                cashRegisterService.getOpenCashRegistersFromPreviousDays('all')
            ]);
            setCashRegisters(registersData);
            setCashFlows(flowsData);
            setOrders(ordersData);
            setPendingRegisters(pendingData);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const getBranchName = (branchOfficeId?: string) =>
        branchOffices.find((branchOffice) => branchOffice.id === branchOfficeId)?.name || 'Sucursal';

    useEffect(() => {
        void getData();
    }, [selectedDay]);

    const summaries = useMemo(
        () =>
            buildBranchCashSummaries(branchOffices, cashRegisters, cashFlows, orders, {
                dayEnd: endOfDay(selectedDay)
            }),
        [branchOffices, cashRegisters, cashFlows, orders, selectedDay]
    );
    const totals = useMemo(() => getCashRegistersDayTotals(summaries), [summaries]);

    const rows: CashRow[] = useMemo(
        () =>
            summaries.flatMap((branch): CashRow[] => {
                const registerRows: CashRow[] = branch.registers.map((summary, index) => ({
                    id: summary.register.id as string,
                    kind: 'register',
                    branchOfficeId: branch.branchOfficeId,
                    branchOfficeName: branch.branchOfficeName,
                    showBranchName: index === 0,
                    register: summary.register,
                    isOpen: summary.isOpen,
                    from: summary.from,
                    to: summary.to,
                    initialCash: summary.initialCash,
                    cashSales: summary.cashSales,
                    otherInputs: summary.otherInputs,
                    otherOutputs: summary.otherOutputs,
                    shipping: summary.shipping,
                    expectedCash: summary.expectedCash,
                    countedCash: summary.countedCash,
                    difference: summary.difference,
                    hasBlindCount: summary.hasBlindCount,
                    ordersCount: summary.ordersCount
                }));

                // Renglón extra SOLO si hubo dinero fuera de toda caja — eso sí
                // hay que verlo: es "se vendió sin caja abierta".
                //
                // ⚠️ Una sucursal **sin caja y sin movimientos no se lista**
                // (2026-08-29, pedido de Irving): antes aparecía con un renglón
                // en ceros solo para ofrecer el botón de abrir caja, y con nueve
                // sucursales eso llenaba la tabla de filas que no dicen nada.
                // Abrir caja en un día pasado se hace desde el botón de arriba,
                // que pregunta la sucursal.
                const outside = branch.outside;
                const hasOutside = outside.ordersCount > 0 || outside.expectedCash !== 0;
                if (!hasOutside) return registerRows;

                return [
                    ...registerRows,
                    {
                        id: `fuera-${branch.branchOfficeId}`,
                        kind: 'outside',
                        branchOfficeId: branch.branchOfficeId,
                        branchOfficeName: branch.branchOfficeName,
                        showBranchName: registerRows.length === 0,
                        isOpen: false,
                        initialCash: 0,
                        cashSales: outside.cashSales,
                        otherInputs: outside.otherInputs,
                        otherOutputs: outside.otherOutputs,
                        shipping: outside.shipping,
                        expectedCash: outside.expectedCash,
                        hasBlindCount: false,
                        ordersCount: outside.ordersCount
                    }
                ];
            }),
        [summaries]
    );

    const onOpenCreate = (branchOfficeId?: string) => {
        setEditing(undefined);
        setCreatingForBranch(branchOfficeId);
        setModalOpen(true);
    };

    const onOpenEdit = (cashRegister: CashRegister) => {
        setEditing(cashRegister);
        setCreatingForBranch(undefined);
        setModalOpen(true);
    };

    const onSubmitModal = async (values: { branchOfficeId: string; date: Date; initialCash: number }) => {
        showLoader();
        try {
            if (editing?.id) {
                await cashRegisterService.updateCashRegister(editing.id, {
                    initialCash: values.initialCash,
                    date: values.date
                });
                showAlertMessage('Caja actualizada');
            } else {
                await cashRegisterService.createCashRegisterForDate(values);
                showAlertMessage('Caja abierta');
            }
            setModalOpen(false);
            await getData();
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onCloseCashRegister = (row: CashRow) => {
        if (!row.register?.id) return;
        showIOSAlert({
            title: `Cerrar la caja de ${row.branchOfficeName}`,
            message:
                `Efectivo esperado de este turno: ${currencyFormatter(row.expectedCash)}\n\n` +
                'Se cierra SIN corte ciego (no hay conteo físico de por medio): el corte lo hace quien cierra ' +
                'desde su propia caja. Úsalo para cajas que quedaron abiertas por olvido.',
            buttons: [
                { text: 'Cancelar', style: 'cancel', onPress: () => {} },
                {
                    text: 'Cerrar caja',
                    onPress: async () => {
                        showLoader();
                        try {
                            await cashRegisterService.closeCashRegister(row.register?.id as string);
                            // Si el admin cerró la caja que su propia sesión tiene abierta,
                            // hay que sacarla de Redux o el POS seguiría creyendo que opera
                            // con una caja ya cerrada.
                            if (activeCashRegister?.id === row.register?.id) {
                                dispatch(setCashRegister(undefined));
                            }
                            showAlertMessage('Caja cerrada');
                            await getData();
                        } catch (error: any) {
                            showAlertError(error);
                        }
                        hideLoader();
                    }
                }
            ]
        });
    };

    const onReopenCashRegister = (row: CashRow) => {
        if (!row.register?.id) return;
        showIOSAlert({
            title: `Reabrir la caja de ${row.branchOfficeName}`,
            message: 'La caja volverá a quedar abierta para poder seguir registrando movimientos del día.',
            buttons: [
                { text: 'Cancelar', style: 'cancel', onPress: () => {} },
                {
                    text: 'Reabrir',
                    onPress: async () => {
                        showLoader();
                        try {
                            await cashRegisterService.reopenCashRegister(row.register?.id as string);
                            showAlertMessage('Caja reabierta');
                            await getData();
                        } catch (error: any) {
                            showAlertError(error);
                        }
                        hideLoader();
                    }
                }
            ]
        });
    };

    const header = (label: string) => () =>
        (
            <Typography color="text.secondary" fontWeight={600}>
                {label}
            </Typography>
        );

    const columns: GridColDef[] = [
        {
            field: 'branchOfficeName',
            flex: 1.6,
            minWidth: 150,
            renderHeader: header('Sucursal'),
            renderCell: ({ row }) => (
                <Typography fontWeight={600} noWrap>
                    {row.showBranchName ? row.branchOfficeName : ''}
                </Typography>
            )
        },
        {
            field: 'turno',
            flex: 1.5,
            minWidth: 140,
            sortable: false,
            renderHeader: header('Turno'),
            renderCell: ({ row }) =>
                row.kind === 'outside' ? (
                    <Typography color="text.disabled">—</Typography>
                ) : (
                    <Stack>
                        <Typography variant="body2">
                            {`${format(row.from, 'HH:mm')} → ${
                                row.register?.closedAt ? format(getCashRegisterDate(row.register.closedAt), 'HH:mm') : '···'
                            }`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {row.register?.username || 'Sin responsable'}
                        </Typography>
                    </Stack>
                )
        },
        {
            field: 'estado',
            flex: 1,
            minWidth: 105,
            sortable: false,
            renderHeader: header('Estado'),
            renderCell: ({ row }) =>
                row.kind === 'outside' ? (
                    <Typography color="text.disabled">—</Typography>
                ) : row.isOpen ? (
                    <Chip size="small" color="success" label="Abierta" />
                ) : (
                    <Chip size="small" variant="outlined" label="Cerrada" />
                )
        },
        {
            field: 'initialCash',
            flex: 1,
            minWidth: 100,
            renderHeader: header('Fondo'),
            renderCell: ({ row }) =>
                row.kind === 'outside' ? (
                    <Typography color="text.disabled">—</Typography>
                ) : (
                    <Typography>{currencyFormatter(row.initialCash)}</Typography>
                )
        },
        {
            field: 'cashSales',
            flex: 1.1,
            minWidth: 120,
            renderHeader: header('Venta efectivo'),
            renderCell: ({ row }) => <Typography>{currencyFormatter(row.cashSales)}</Typography>
        },
        {
            field: 'expectedCash',
            flex: 1.2,
            minWidth: 125,
            renderHeader: header('Esperado'),
            renderCell: ({ row }) => (
                <Tooltip
                    title={`Fondo ${currencyFormatter(row.initialCash)} + efectivo ${currencyFormatter(row.cashSales)} + otros ingresos ${currencyFormatter(
                        row.otherInputs
                    )} − otros gastos ${currencyFormatter(row.otherOutputs)} − envíos ${currencyFormatter(row.shipping)}`}
                >
                    <Typography fontWeight={600}>{currencyFormatter(row.expectedCash)}</Typography>
                </Tooltip>
            )
        },
        {
            field: 'countedCash',
            flex: 1.1,
            minWidth: 115,
            renderHeader: header('Contado'),
            renderCell: ({ row }) =>
                row.hasBlindCount ? (
                    <Typography>{currencyFormatter(row.countedCash)}</Typography>
                ) : (
                    <Typography color="text.disabled">—</Typography>
                )
        },
        {
            field: 'difference',
            flex: 1.1,
            minWidth: 120,
            renderHeader: header('Diferencia'),
            renderCell: ({ row }) => {
                if (!row.hasBlindCount) return <Typography color="text.disabled">—</Typography>;
                const value = row.difference || 0;
                if (value === 0) return <Chip size="small" color="success" variant="outlined" label="Cuadra" />;
                return (
                    <Typography fontWeight={700} color={value < 0 ? 'error.main' : 'warning.main'}>
                        {`${value > 0 ? '+' : '−'}${currencyFormatter(Math.abs(value))}`}
                    </Typography>
                );
            }
        },
        {
            field: 'acciones',
            flex: 1.1,
            minWidth: 120,
            sortable: false,
            disableExport: true,
            renderHeader: header('Acciones'),
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={0.5}>
                    {row.kind === 'outside' ? (
                        <Tooltip title={`Abrir caja de ${row.branchOfficeName} este día`}>
                            {/* Sin color="primary": el tema lo convierte en botón sólido
                                oscuro y repetido por renglón se come la tabla */}
                            <IconButton size="small" onClick={() => onOpenCreate(row.branchOfficeId)}>
                                <AddCircleOutline />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <>
                            {/* La bitácora de la caja. Va AQUÍ porque éste es el
                                lugar donde un admin viene a revisar un corte que no
                                cuadra — hasta el 2026-09-11 el único botón de
                                actividad de cajas vivía en un modal del informe de
                                ventas, o sea el historial existía y no se podía
                                consultar desde donde se investiga (D5).
                                ⚠️ SIN renglón derivado (D8): la caja ya registra
                                "abrió la caja con el monto de X" — derivarlo sería
                                duplicar un rastro que ya existe (D6). */}
                            <Tooltip title="Ver actividad de esta caja">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        dispatch(
                                            setActivityDrawer({
                                                isOpen: true,
                                                section: 'cashRegister',
                                                sectionId: row.register?.id
                                            })
                                        )
                                    }
                                >
                                    <HistoryIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar fondo y fecha">
                                <IconButton size="small" onClick={() => onOpenEdit(row.register)}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            {row.isOpen ? (
                                <Button size="small" color="error" onClick={() => onCloseCashRegister(row)}>
                                    Cerrar
                                </Button>
                            ) : (
                                <Tooltip title="Reabrir caja">
                                    <IconButton size="small" onClick={() => onReopenCashRegister(row)}>
                                        <LockOpen />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    )}
                </Stack>
            )
        }
    ];

    return (
        <Stack height="100%" minHeight={0} spacing={2}>
            {/* Filtro: un día, todas las sucursales */}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* Mismo selector que el filtro de órdenes, en modo un solo día */}
                <Box width={230}>
                    <RangeDatePicker
                        fullWidth
                        singleDay
                        selectedDate={selectedDay}
                        value={format(selectedDay, 'dd MMMM, yyyy', { locale: es })}
                        onChangeDay={(day) => setSelectedDay(startOfDay(day))}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {`${totals.branchesWithRegister} de ${summaries.length} sucursales abrieron caja`}
                    {totals.openRegisters > 0 ? ` · ${totals.openRegisters} sin cerrar` : ''}
                </Typography>
                <Box flex={1} />
                <Tooltip title="Abrir una caja en el día seleccionado">
                    <IconButton
                        color="primary"
                        onClick={() => onOpenCreate(undefined)}
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                        <AddCircleOutline />
                    </IconButton>
                </Tooltip>
                <IconButton color="secondary" onClick={getData} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Replay />
                </IconButton>
            </Stack>

            {/* Cajas de días pasados sin cerrar. NO se bloquea abrir caja nueva por
                ellas (quien llega hoy no puede cerrar la caja de ayer de alguien más),
                así que el control es que el admin las vea aquí y las cierre. */}
            {pendingRegisters.length > 0 && (
                <Alert severity="warning" variant="outlined" sx={{ flexShrink: 0, '& .MuiAlert-message': { minWidth: 0, width: '100%' } }}>
                    <Typography variant="body2" fontWeight={700} gutterBottom>
                        {pendingRegisters.length === 1
                            ? 'Hay 1 caja de un día anterior sin cerrar'
                            : `Hay ${pendingRegisters.length} cajas de días anteriores sin cerrar`}
                        {pendingRegisters.length > 1 &&
                            ` · de ${format(getCashRegisterDate(pendingRegisters[pendingRegisters.length - 1].date), 'dd MMM yyyy', { locale: es })} a ${format(getCashRegisterDate(pendingRegisters[0].date), 'dd MMM yyyy', { locale: es })}`}
                    </Typography>
                    {/* ⚠️ **Una sola fila con scroll HORIZONTAL** (pedido de Irving,
                        2026-08-29). Envolviendo en varias filas, un rezago de decenas
                        de cajas crecía hacia abajo y le comía el alto a la tabla, que
                        es lo que se viene a ver. Así el aviso ocupa siempre lo mismo,
                        haya 1 caja o 30. */}
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 0.5, '& > *': { flexShrink: 0 } }}
                    >
                        {pendingRegisters.slice(0, MAX_PENDING_CHIPS).map((register) => (
                            <Chip
                                key={register.id}
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={`${getBranchName(register.branchOfficeId)} · ${format(
                                    getCashRegisterDate(register.date),
                                    'dd MMM',
                                    { locale: es }
                                )} · ${register.username || 'sin responsable'}`}
                                onClick={() => setSelectedDay(startOfDay(getCashRegisterDate(register.date)))}
                                onDelete={() =>
                                    onCloseCashRegister({
                                        id: register.id as string,
                                        kind: 'register',
                                        branchOfficeId: register.branchOfficeId as string,
                                        branchOfficeName: getBranchName(register.branchOfficeId),
                                        showBranchName: true,
                                        register,
                                        isOpen: true,
                                        initialCash: register.initialCash || 0,
                                        cashSales: 0,
                                        otherInputs: 0,
                                        otherOutputs: 0,
                                        shipping: 0,
                                        expectedCash: register.initialCash || 0,
                                        hasBlindCount: false,
                                        ordersCount: 0
                                    })
                                }
                                deleteIcon={
                                    <Tooltip title="Cerrar esta caja">
                                        <LockOpen />
                                    </Tooltip>
                                }
                            />
                        ))}
                    </Stack>
                    {pendingRegisters.length > MAX_PENDING_CHIPS && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                            {`Se muestran ${MAX_PENDING_CHIPS} de ${pendingRegisters.length}. Para un rezago así conviene cerrarlas en bloque: scripts/cerrar-cajas-abiertas.mjs`}
                        </Typography>
                    )}
                </Alert>
            )}

            {/* Los KPI del día se QUITARON el 2026-08-29 (pedido de Irving): esta
                pantalla se abre para trabajar las cajas una por una, y sus cuatro
                tarjetas le robaban a la tabla el alto que necesita. Los mismos
                números están en el informe de ventas, que es donde se consultan.
                `totals` se sigue calculando: lo usan otras partes de la vista. */}

            <Box flex={1} minHeight={0}>
                <DataGrid
                    sx={{
                        border: 0,
                        height: '100%',
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F4F6F8' }
                    }}
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    pageSizeOptions={[10, 25, 50]}
                    disableColumnSelector
                    disableColumnFilter
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
                />
            </Box>

            <ModalCashRegisterAdmin
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                cashRegister={editing}
                branchOfficeId={creatingForBranch}
                branchOffices={branchOffices}
                defaultDate={
                    // Día seleccionado: si es hoy, con la hora actual; si es pasado, a las 9:00
                    format(selectedDay, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                        ? new Date()
                        : new Date(selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate(), 9, 0)
                }
                onSubmit={onSubmitModal}
            />
        </Stack>
    );
};

export default CashRegistersTab;
